/**
 * Appointment Results — Cockpit skin
 * ──────────────────────────────────
 * Jobs-era restraint pass over the Classic AppointmentResults page.
 *
 * Same data, same routes, same filters. Restructured for editorial calm:
 *   • Real type scale (display headline, lead deck, body)
 *   • Hairline rules (border-b) instead of bordered cards/tints
 *   • One emphasis per view — Close Rate is the hero metric
 *   • Icon density cut ~70%; symbols only where they carry information
 *   • Single signature interaction: row → /appointment-outcome/:id
 *
 * No business logic changes vs. AppointmentResults.tsx.
 */
import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { OBJECTION_LABELS } from "@/data/appointmentOutcomeTypes";
import { lifecycleToneClasses } from "@/data/situational/outcomeCapture";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format, parseISO, startOfDay, endOfDay } from "date-fns";
import { Search, Calendar as CalendarIcon, ChevronRight } from "lucide-react";
import {
  RESULT_TYPE_CONFIG, NO_SALE_REASON_LABELS, NO_DEMO_REASON_LABELS,
  type AppointmentResult, type AppointmentResultType,
} from "@/data/appointmentResultTypes";
import { getResultMetrics, getRepPerformanceSummary, getDisplayPrice } from "@/data/appointmentResultSelectors";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";

const RESULT_DOT: Record<AppointmentResultType, string> = {
  sold: "bg-success",
  no_sale: "bg-destructive",
  rescheduled: "bg-warning",
  no_demo: "bg-muted-foreground/60",
};

const repLabel = (value: string) => resolveProfileDisplayName(value, undefined, "Unassigned");

const TABS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All" },
  { value: "sold", label: "Sold" },
  { value: "no_sale", label: "No sale" },
  { value: "no_demo", label: "No demo" },
  { value: "rescheduled", label: "Rescheduled" },
];

export default function AppointmentResultsCockpit() {
  const { appointmentResults, appointmentOutcomeExtensions } = useAppData();
  const navigate = useNavigate();

  const extensionsByResultId = useMemo(() => {
    const m = new Map<string, typeof appointmentOutcomeExtensions[number]>();
    for (const ext of appointmentOutcomeExtensions) m.set(ext.appointmentResultId, ext);
    return m;
  }, [appointmentOutcomeExtensions]);

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const reps = useMemo(
    (): string[] => [...new Set(appointmentResults.map((r: AppointmentResult) => r.assignedRep))].sort(),
    [appointmentResults]
  );
  const sources = useMemo(
    (): string[] => [...new Set(appointmentResults.map((r: AppointmentResult) => r.source).filter(Boolean))].sort(),
    [appointmentResults]
  );

  const filtered = useMemo(() => {
    let list = appointmentResults;
    if (tab !== "all") list = list.filter(r => r.resultType === tab);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.customerName.toLowerCase().includes(q) || repLabel(r.assignedRep).toLowerCase().includes(q));
    }
    if (repFilter !== "all") list = list.filter(r => r.assignedRep === repFilter);
    if (sourceFilter !== "all") list = list.filter(r => r.source === sourceFilter);
    if (dateFrom) list = list.filter(r => new Date(r.appointmentDatetime) >= startOfDay(dateFrom));
    if (dateTo) list = list.filter(r => new Date(r.appointmentDatetime) <= endOfDay(dateTo));
    return list.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [appointmentResults, tab, search, repFilter, sourceFilter, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const m = getResultMetrics(appointmentResults);
    return {
      total: m.total, sold: m.sold, noSale: m.noSale, rescheduled: m.rescheduled,
      noDemo: m.noDemo, totalRevenue: m.totalRevenue, closeRate: m.closeRatePercent,
    };
  }, [appointmentResults]);

  const filtersActive = !!(search || repFilter !== "all" || sourceFilter !== "all" || dateFrom || dateTo);

  const pagination = usePagination(filtered, 25);
  useEffect(() => { pagination.reset(); }, [tab, search, repFilter, sourceFilter, dateFrom, dateTo]);

  return (
    <div className="max-w-[1320px] mx-auto px-6 py-6 space-y-6">
      {/* ── Masthead: compact, operator-grade ───────────────────────── */}
      <header className="flex flex-wrap items-end justify-between gap-6 pb-5 border-b border-border/60">
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            Sales · Record of outcomes
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Appointment Results
          </h1>
        </div>
        {/* Hero metric inline, anchored right — single emphasis preserved */}
        <div className="flex items-end gap-7">
          <HeroMetric label="Close rate" value={`${stats.closeRate}%`} hero />
          <span className="h-9 w-px bg-border/60 self-center" />
          <HeroMetric label="Sold" value={stats.sold} />
          <HeroMetric label="No sale" value={stats.noSale} />
          <HeroMetric label="Rescheduled" value={stats.rescheduled} />
          <HeroMetric
            label="Revenue"
            value={`$${(stats.totalRevenue / 1000).toFixed(stats.totalRevenue >= 100_000 ? 0 : 1)}k`}
          />
        </div>
      </header>

      {/* ── Controls: tabs + filters ─────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border/60">
          {TABS.map(t => {
            const count =
              t.value === "all" ? appointmentResults.length :
              t.value === "sold" ? stats.sold :
              t.value === "no_sale" ? stats.noSale :
              t.value === "no_demo" ? stats.noDemo :
              stats.rescheduled;
            const active = tab === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={cn(
                  "relative pb-3 text-sm transition-colors tabular-nums",
                  active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label} <span className="text-muted-foreground/70 ml-0.5">{count}</span>
                {active && <span className="absolute -bottom-px left-0 right-0 h-px bg-foreground" />}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer or rep"
              className="pl-9 h-9 text-sm bg-transparent border-border/60 focus-visible:ring-0 focus-visible:border-foreground/40"
            />
          </div>
          <Select value={repFilter} onValueChange={setRepFilter}>
            <SelectTrigger className="h-9 w-44 text-sm bg-transparent border-border/60"><SelectValue placeholder="All reps" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-sm">All reps</SelectItem>
              {reps.map(r => <SelectItem key={r} value={r} className="text-sm">{repLabel(r)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-9 w-40 text-sm bg-transparent border-border/60"><SelectValue placeholder="All sources" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-sm">All sources</SelectItem>
              {sources.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 text-sm gap-1.5 bg-transparent border-border/60">
                <CalendarIcon className="h-3.5 w-3.5" />
                {dateFrom ? format(dateFrom, "MMM d") : "From"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 text-sm gap-1.5 bg-transparent border-border/60">
                <CalendarIcon className="h-3.5 w-3.5" />
                {dateTo ? format(dateTo, "MMM d") : "To"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
            </PopoverContent>
          </Popover>
          {filtersActive && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => { setSearch(""); setRepFilter("all"); setSourceFilter("all"); setDateFrom(undefined); setDateTo(undefined); }}
            >
              Clear
            </Button>
          )}
        </div>
      </section>

      {/* ── Results list ─────────────────────────────────────────────── */}
      <section>
        {filtered.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-base text-muted-foreground">No appointment results match the current filters.</p>
          </div>
        ) : (
          <div className="divide-y divide-border/60 border-y border-border/60">
            {/* Header rule */}
            <div className="hidden md:grid grid-cols-[2fr_1.2fr_1fr_1fr_1.2fr_1.4fr_auto] gap-6 py-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
              <span>Customer</span>
              <span>Rep</span>
              <span>Outcome</span>
              <span>Lifecycle</span>
              <span>Pricing</span>
              <span>Reason · Next step</span>
              <span className="w-6" />
            </div>

            {pagination.paged.map(r => {
              const ext = extensionsByResultId.get(r.id);
              const cfg = RESULT_TYPE_CONFIG[r.resultType];
              const reasonLabel = r.noSaleReason
                ? (NO_SALE_REASON_LABELS[r.noSaleReason] || r.noSaleReason)
                : r.noDemoReason
                  ? (NO_DEMO_REASON_LABELS[r.noDemoReason] || r.noDemoReason)
                  : null;
              const objectionLabel = ext?.objections?.primary ? OBJECTION_LABELS[ext.objections.primary] : null;
              const lifecycleTone = ext ? lifecycleToneClasses(ext.lifecycle) : null;

              return (
                <Link
                  key={r.id}
                  to={`/appointment-outcome/${r.id}`}
                  className="group block px-1 py-3 transition-colors hover:bg-muted/30 focus:outline-none focus:bg-muted/30"
                >
                  <div className="md:grid md:grid-cols-[2fr_1.2fr_1fr_1fr_1.2fr_1.4fr_auto] md:gap-6 md:items-center space-y-1 md:space-y-0">
                    {/* Customer */}
                    <div className="min-w-0">
                      <p className="text-sm text-foreground font-medium leading-tight truncate">{r.customerName}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {r.appointmentDatetime ? format(parseISO(r.appointmentDatetime), "MMM d · h:mm a") : "No date"}
                        {r.source && <> · {r.source}</>}
                      </p>
                    </div>

                    {/* Rep */}
                    <p className="text-sm text-foreground/90 truncate">{repLabel(r.assignedRep)}</p>

                    {/* Outcome */}
                    <div className="flex items-center gap-2">
                      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", RESULT_DOT[r.resultType])} />
                      <span className="text-sm text-foreground/90">{cfg.label}</span>
                    </div>

                    {/* Lifecycle */}
                    <div>
                      {lifecycleTone ? (
                        <span className={cn("inline-flex items-center gap-1.5 text-xs", lifecycleTone.text)}>
                          <span className={cn("h-1.5 w-1.5 rounded-full", lifecycleTone.dot)} />
                          {lifecycleTone.label}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/60">—</span>
                      )}
                    </div>

                    {/* Pricing — canonical: SOLD reads from Job.financials.contractValue */}
                    <div className="text-sm tabular-nums">
                      {r.resultType === "sold" ? (
                        <span className="text-foreground font-medium">${getDisplayPrice(r, undefined).toLocaleString()}</span>
                      ) : ext?.proposal ? (
                        <span className="text-foreground font-medium">${ext.proposal.finalPrice.toLocaleString()}</span>
                      ) : r.pricingSnapshot ? (
                        <span className="text-foreground font-medium">${r.pricingSnapshot.bestValuePrice.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground/60 text-xs">No build</span>
                      )}
                    </div>

                    {/* Reason · Next step — single line, truncate */}
                    <div className="text-sm min-w-0">
                      <p className="text-foreground/90 leading-tight truncate">
                        {objectionLabel || reasonLabel ||
                          (r.resultType === "rescheduled" && r.rescheduleDatetime
                            ? `Rescheduled to ${format(parseISO(r.rescheduleDatetime), "MMM d")}`
                            : r.resultType === "sold" ? "Closed" : "—")}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{r.nextAction}</p>
                    </div>

                    {/* Affordance */}
                    <ChevronRight className="hidden md:block h-4 w-4 text-muted-foreground/40 group-hover:text-foreground transition-colors" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        <PaginationBar {...pagination} />
      </section>

      {/* ── Rep performance ──────────────────────────────────────────── */}
      {appointmentResults.length > 0 && (
        <section className="space-y-4 pt-6">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            Rep performance
          </p>
          <div className="border-y border-border/60 divide-y divide-border/60">
            <div className="hidden md:grid grid-cols-[2fr_repeat(5,1fr)_1.2fr] gap-6 py-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
              <span>Rep</span>
              <span className="text-center">Total</span>
              <span className="text-center">Sold</span>
              <span className="text-center">No sale</span>
              <span className="text-center">No demo</span>
              <span className="text-center">Close</span>
              <span className="text-right">Revenue</span>
            </div>
            {getRepPerformanceSummary(appointmentResults).map(r => (
              <div key={r.rep} className="grid grid-cols-[2fr_repeat(5,1fr)_1.2fr] gap-6 py-3 items-center text-sm">
                <span className="text-foreground font-medium">{repLabel(r.rep)}</span>
                <span className="text-center text-foreground/90 tabular-nums">{r.total}</span>
                <span className="text-center text-foreground tabular-nums font-medium">{r.sold}</span>
                <span className="text-center text-muted-foreground tabular-nums">{r.noSale}</span>
                <span className="text-center text-muted-foreground tabular-nums">{r.noDemo}</span>
                <span className={cn(
                  "text-center tabular-nums font-medium",
                  r.closeRate >= 30 ? "text-success" : r.closeRate >= 15 ? "text-warning" : "text-destructive"
                )}>{r.closeRate}%</span>
                <span className="text-right text-foreground tabular-nums font-medium">${r.revenue.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function HeroMetric({
  label,
  value,
  hero = false,
}: { label: string; value: string | number; hero?: boolean }) {
  return (
    <div className="text-right">
      <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">{label}</p>
      <p className={cn(
        "tabular-nums tracking-tight text-foreground font-semibold leading-none",
        hero ? "text-3xl" : "text-xl text-foreground/90 font-medium",
      )}>{value}</p>
    </div>
  );
}
